import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Pagination,
  Tooltip,
  Typography,
} from '@mui/material'
import { Collapse } from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import GetAppIcon from '@mui/icons-material/GetApp'
import { useTheme } from '../../../contexts/ThemeContext'
import SectionHeader from '@/components/shared/SectionHeader'
import TransactionHistoryCard from './TransactionHistoryCard'
import TransactionFilters from './TransactionFilters'
import type { TransactionHistoryItem } from './types'
import type { ElementType } from 'react'
import type { TextFieldProps } from '@mui/material'
import type { Chain } from 'viem'

interface TransactionHistoryProps {
  transactions: TransactionHistoryItem[]
  isLoading: boolean
  currentPage: number
  totalPages: number
  showFilters: boolean
  filterDateFrom: string
  filterDateTo: string
  filterNonce: string
  filterType: string
  chain: Chain | undefined
  isMobileOrTablet: boolean
  decimals: number
  StyledTextField: ElementType<TextFieldProps>
  onRefresh: () => void
  onToggleFilters: () => void
  onPageChange: (page: number) => void
  onDateFromChange: (date: string) => void
  onDateToChange: (date: string) => void
  onNonceChange: (nonce: string) => void
  onTypeChange: (type: string) => void
  onClearFilters: () => void
  onApplyFilters: () => void
  onCopyAddress: (address: string) => void
  onExportClick: () => void
}

export default function TransactionHistory({
  transactions,
  isLoading,
  currentPage,
  totalPages,
  showFilters,
  filterDateFrom,
  filterDateTo,
  filterNonce,
  filterType,
  chain,
  isMobileOrTablet,
  decimals,
  StyledTextField,
  onRefresh,
  onToggleFilters,
  onPageChange,
  onDateFromChange,
  onDateToChange,
  onNonceChange,
  onTypeChange,
  onClearFilters,
  onApplyFilters,
  onCopyAddress,
  onExportClick,
}: TransactionHistoryProps) {
  const { colors } = useTheme()

  return (
    <Box sx={{ mt: 3 }}>
      <SectionHeader
        title="Transactions History"
        action={
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              variant="outlined"
              size="small"
              onClick={onToggleFilters}
              sx={{
                borderColor: colors.primary,
                color: colors.primary,
                '&:hover': {
                  borderColor: colors.primary,
                  backgroundColor: `${colors.primary}20`,
                },
              }}
            >
              {showFilters ? 'Hide filters' : 'Show filters'}
            </Button>
            <Tooltip title="Export Transactions">
              <span>
                <IconButton
                  onClick={onExportClick}
                  sx={{
                    color: colors.primary,
                    '&:hover': {
                      backgroundColor: `${colors.primary}22`,
                    },
                  }}
                >
                  <GetAppIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Refresh Transaction History">
              <span>
                <IconButton
                  onClick={onRefresh}
                  disabled={isLoading}
                  sx={{
                    color: colors.primary,
                    '&:hover': {
                      backgroundColor: `${colors.primary}22`,
                    },
                    '&:disabled': {
                      color: colors.text,
                      opacity: 0.5,
                    },
                  }}
                >
                  <RefreshIcon
                    sx={{
                      animation: isLoading ? 'spin 1s linear infinite' : 'none',
                      '@keyframes spin': {
                        '0%': {
                          transform: 'rotate(0deg)',
                        },
                        '100%': {
                          transform: 'rotate(360deg)',
                        },
                      },
                    }}
                  />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        }
      />
      <Collapse in={showFilters}>
        <Box
          sx={{
            backgroundColor: colors.overlay,
            borderRadius: 2,
            p: 2,
            mb: 2,
            border: `1px solid ${colors.primary}`,
          }}
        >
          <TransactionFilters
            filterDateFrom={filterDateFrom}
            filterDateTo={filterDateTo}
            filterNonce={filterNonce}
            filterType={filterType}
            onDateFromChange={onDateFromChange}
            onDateToChange={onDateToChange}
            onNonceChange={onNonceChange}
            onTypeChange={onTypeChange}
            onClear={onClearFilters}
            onApply={onApplyFilters}
            StyledTextField={StyledTextField}
          />
        </Box>
      </Collapse>
      <Box
        sx={{
          backgroundColor: colors.overlay,
          borderRadius: 2,
          p: 2,
          border: `1px solid ${colors.primary}`,
        }}
      >
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 4,
            }}
          >
            <CircularProgress sx={{ color: colors.primary }} />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {transactions.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  py: 4,
                }}
              >
                <Typography
                  sx={{
                    color: colors.text,
                    opacity: 0.7,
                    fontStyle: 'italic',
                  }}
                >
                  No transaction history
                </Typography>
              </Box>
            ) : (
              <>
                {transactions.map((transaction) => (
                  <TransactionHistoryCard
                    key={transaction.hash}
                    transaction={transaction}
                    chain={chain}
                    isMobileOrTablet={isMobileOrTablet}
                    decimals={decimals}
                    onCopyAddress={onCopyAddress}
                  />
                ))}
                {transactions.length > 0 && totalPages > 1 && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      mt: 2,
                    }}
                  >
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={(_, page) => onPageChange(page)}
                      sx={{
                        '& .MuiPaginationItem-root': {
                          color: colors.text,
                        },
                        '& .Mui-selected': {
                          backgroundColor: `${colors.primary}33`,
                          color: colors.primary,
                        },
                        '& .MuiPaginationItem-root:hover': {
                          backgroundColor: `${colors.primary}20`,
                        },
                      }}
                    />
                  </Box>
                )}
              </>
            )}
          </Box>
        )}
      </Box>
    </Box>
  )
}
